import API from './ApiBase';

export type Ll144Role = 'EMPLOYER' | 'EMPLOYMENT_AGENCY';

export type Ll144SubjectType =
  | 'CANDIDATES'
  | 'EMPLOYEES'
  | 'CANDIDATES_AND_EMPLOYEES';

// Where the position sits. Local Law 144 scopes on the position's location, never on
// where the assessed people live, so every value names an office rather than a person.
// REMOTE_NOT_NYC_ASSOCIATED is phrased negatively so it also covers a fully distributed
// employer that has no office to associate a remote role with.
export type Ll144PositionLocation =
  | 'NYC_OFFICE'
  | 'REMOTE_NYC_OFFICE'
  | 'REMOTE_NOT_NYC_ASSOCIATED'
  | 'NON_NYC_OFFICE';

// Request payload is camelCase (backend SubmitLl144AssessmentDto).
export type SubmitLl144AssessmentDto = {
  role: Ll144Role;
  usesAedt: boolean;
  subjectType: Ll144SubjectType;
  substantialAssistanceOrReplacement: boolean;
  positionLocation: Ll144PositionLocation;
  // Only sent for employment agencies; omitted entirely for employers, so the API never
  // records an answer to a question the user was not shown.
  agencyLocatedInNyc?: boolean;
  // Determines who must receive the 10-business-day notice. Not a scope input.
  assessedPeopleResideInNyc: boolean;
  // YYYY-MM-DD. The second half of the 10-business-day notice window; the first is the date
  // recorded with the notice evidence. May be in the FUTURE — a planned first use is exactly when
  // the window still matters — so it must not be clamped to today like every other date field.
  //
  // Omitted entirely when the wizard does not ask (a non-AEDT tool, or one that screens nobody
  // resident in NYC), matching how agencyLocatedInNyc is handled. Absence means PRESERVE on the
  // server; only an explicit null clears a stored date.
  aedtFirstUseDate?: string | null;
};

export type Ll144AssessmentResponse = {
  aiSystemId: string;
  regulationCode: 'NYC_LL144';
  classification: 'IN_SCOPE' | 'OUT_OF_SCOPE';
  classificationLabel: string;
  complianceStatus: string;
  obligationsCreated: number;
};

export type UpdateLl144FirstUseDateResponse = {
  aiSystemId: string;
  aedtFirstUseDate: string | null;
};

/**
 * Set or clear the AEDT first-use date on its own.
 *
 * Separate from the assessment submit because re-running the assessment re-realizes the system's
 * requirement roadmap — far too much for editing one date — and because every system that predates
 * the field has none, so there has to be a way to supply it after the fact.
 *
 * `null` clears the stored date. Unlike the submit DTO there is no "omit to preserve" case here:
 * this endpoint exists to set the value.
 */
export const updateLl144FirstUseDate = async (
  aiSystemId: string,
  aedtFirstUseDate: string | null
): Promise<UpdateLl144FirstUseDateResponse> => {
  try {
    const response = await API.patch<UpdateLl144FirstUseDateResponse>(
      `/ai-systems/${aiSystemId}/assessment/ll144/first-use-date`,
      { aedtFirstUseDate }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating LL144 first use date:', error);
    throw error;
  }
};

export const submitLl144Assessment = async (
  aiSystemId: string,
  dto: SubmitLl144AssessmentDto
): Promise<Ll144AssessmentResponse> => {
  try {
    const response = await API.post<Ll144AssessmentResponse>(
      `/ai-systems/${aiSystemId}/assessment/ll144`,
      dto
    );
    return response.data;
  } catch (error) {
    console.error('Error submitting LL144 assessment:', error);
    throw error;
  }
};
